import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, EMPTY, NEVER, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import {
    ActionItem,
    ActionItemStyle,
    BaseContainer,
    NavigationLink,
    RequestPaginationInfo,
    SortDirection,
    SortInfo,
    SortOption,
    Toast
} from '@stylehaven/components';
import { NotificationSearch } from '../../../entities/notifications/notification-search.entity';
import { NotificationEntity } from '../../../entities/notifications/notification.entity';
import { SortableFieldsToSortOptions } from '../../../helpers/sortable-fields-to-sort-options.helper';
import { PaginatedResults } from '../../../record/entities/pagination/paginated-results.entity';
import { PaginationInfo } from '../../../record/entities/pagination/pagination-info.entity';
import { NotificationsSandboxService } from '../../sandbox/notifications-sandbox.service';

@Component({
    standalone: false,
    selector: 'digihaven-notification-overview-container',
    templateUrl: './notification-overview-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationsOverviewContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private readonly DEFAULT_PAGE_SIZE: number = 10;
    private readonly SORTABLE_FIELDS: string[] = ['SentOn', 'Body'];
    readonly SORT_OPTIONS: SortOption[];

    private refreshable$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    readonly sortInfo$: BehaviorSubject<SortInfo | null> = new BehaviorSubject<SortInfo | null>(null);

    readonly COLUMN_COUNT: number = 5;

    notifications$!: Observable<PaginatedResults<NotificationEntity>>;
    notificationList$!: Observable<NotificationEntity[]>;
    newNotificationCount$!: Observable<number>;

    notificationSearch!: Observable<NotificationSearch>;
    pagedResult$!: Observable<PaginationInfo>;

    selectAllFormControl!: UntypedFormControl;
    selectedIndexRecordsMap: Map<number, boolean> = new Map();
    actionBarActionItems!: Observable<ActionItem>[];

    tableHeaderWithCount$!: Observable<string>;

    constructor(
        private readonly sandbox: NotificationsSandboxService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router
    ) {
        super();
        this.SORT_OPTIONS = SortableFieldsToSortOptions(
            this.SORTABLE_FIELDS,
            this.translateService,
            'SentOn',
            SortDirection.DESC
        );
    }

    ngOnInit(): void {
        this.setupSourceStream();
        this.initActionitems();
        this.initPagedResult();
        this.setupSelectAllFormControl();
        this.resetSelectionOnRefresh();

        this.markNotificationsOutdatedAfterNotificationFetch();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.refreshable$.complete();
        this.sortInfo$.complete();
    }

    refreshList(): void {
        this.refreshable$.next(true);
    }

    paginationInfoChanged(paginationInfo: RequestPaginationInfo): void {
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {
                ...this.activatedRoute.snapshot.queryParams,
                page: paginationInfo.page,
                size: paginationInfo.resultsPerPage
            }
        });
    }

    getNavigationLink(notification: NotificationEntity): NavigationLink {
        const path = ['records', notification.Action];
        return {
            route: this.router.createUrlTree(path).toString()
        };
    }

    onSortInfoChange(sortInfo: SortInfo): void {
        this.sortInfo$.next(sortInfo);
    }

    deleteNotification(notification: NotificationEntity): void {
        this.sandbox
            .deleteNotification(notification)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(() => this.refreshable$.next(true));
    }

    selectRow(index: number, selected: boolean, notification: NotificationEntity): void {
        this.selectedIndexRecordsMap.set(index, selected);

        this.notificationList$.pipe(take(1)).subscribe(notifications => {
            const allSelected: boolean = notifications.every((_, id) => this.selectedIndexRecordsMap.get(id));
            this.selectAllFormControl.setValue(allSelected, { emitEvent: false });
        });
    }

    onSelectAll(shouldSelect: boolean): void {
        this.selectAllFormControl.setValue(shouldSelect);
    }

    private markNotificationsOutdatedAfterNotificationFetch(): void {
        this.notificationList$
            .pipe(
                takeUntil(this.destroy$),
                switchMap(() => this.sandbox.markNotificationsAsOutdated().pipe(catchError(() => EMPTY)))
            )
            .subscribe();
    }

    private resetSelectionOnRefresh(): void {
        this.notificationList$.pipe(takeUntil(this.destroy$)).subscribe(list => {
            this.selectedIndexRecordsMap.clear();
            list.forEach((_, index) => this.selectedIndexRecordsMap.set(index, false));
            this.selectAllFormControl.setValue(false);
        });
    }

    private initActionitems(): void {
        this.actionBarActionItems = [
            of({
                label: this.translateService.instant('actions.common.delete'),
                style: ActionItemStyle.Inverted,
                eventListener: () => this.deleteSelected()
            })
        ];
    }

    private deleteSelected(): void {
        this.notificationList$
            .pipe(
                take(1),
                map(notifications => notifications.filter((_, index) => this.selectedIndexRecordsMap.get(index))),
                switchMap(notificationsToDelete => this.performDeleteNotifications(notificationsToDelete))
            )
            .subscribe(() => this.refreshable$.next(true));
    }

    private performDeleteNotifications(notificationsToDelete: NotificationEntity[]): Observable<void> {
        const toast: Toast = this.sandbox.createStartDeleteToast(notificationsToDelete.length);
        return this.sandbox.deleteNotifications(notificationsToDelete).pipe(
            take(1),
            tap(() => this.sandbox.createSuccessDeleteToast(toast, notificationsToDelete.length)),
            catchError(() => {
                this.sandbox.createFailDeleteToast(toast, notificationsToDelete.length);
                return NEVER;
            })
        );
    }

    private setupSelectAllFormControl(): void {
        this.selectAllFormControl = new UntypedFormControl(false);

        this.watchselectAllFormControlValue();
    }

    private watchselectAllFormControlValue(): void {
        this.selectAllFormControl.valueChanges
            .pipe(takeUntil(this.destroy$), distinctUntilChanged(isEqual), takeUntil(this.destroy$))
            .subscribe(value => this.emitInteractionOnAllNotifications(value));
    }

    private emitInteractionOnAllNotifications(value: boolean): void {
        this.notificationList$.pipe(take(1)).subscribe(notifications => {
            notifications.forEach((_, index) => this.selectedIndexRecordsMap.set(index, value));
        });
    }

    private setupSourceStream(): void {
        this.notificationSearch = combineLatest([this.sortInfo$, this.activatedRoute.queryParams]).pipe(
            takeUntil(this.destroy$),
            map(([sortInfo, queryParams]) => this.mapParamsToSearch(sortInfo, queryParams)),
            distinctUntilChanged(isEqual)
        );

        this.notifications$ = combineLatest([this.refreshable$, this.notificationSearch]).pipe(
            tap(() => this.startLoading()),
            switchMap(([_, recordSearch]) =>
                this.sandbox.getNotifications(recordSearch).pipe(catchError(() => this.showError()))
            ),
            tap(() => this.stopLoading()),
            takeUntil(this.destroy$),
            catchError(() => this.showError()),
            shareReplay(1)
        );

        this.notificationList$ = this.notifications$.pipe(
            map(notificationPage => notificationPage.results),
            takeUntil(this.destroy$),
            shareReplay(1)
        );

        this.newNotificationCount$ = this.refreshable$.pipe(
            switchMap(() => this.sandbox.getNewNotificationsCount().pipe(catchError(() => EMPTY))),
            takeUntil(this.destroy$),
            shareReplay(1)
        );

        this.tableHeaderWithCount$ = combineLatest([this.notifications$, this.newNotificationCount$]).pipe(
            map(([notifications, newNotificationCount]) => {
                return this.getTableHeaderTitle(notifications, newNotificationCount);
            })
        );
    }

    private getTableHeaderTitle(
        notifications: PaginatedResults<NotificationEntity>,
        newNotificationCount: number
    ): string {
        let baseTitle: string = this.translateService.instant('notifications.table.results', {
            items: notifications.paginationInfo.totalNumberOfResults
        });

        if (newNotificationCount > 0) {
            baseTitle += this.translateService.instant('notifications.table.new-results', {
                items: newNotificationCount
            });
        }

        return baseTitle;
    }

    private initPagedResult(): void {
        this.pagedResult$ = this.notifications$.pipe(map(paginatedResults => paginatedResults.paginationInfo));
    }

    private startLoading(): void {
        this.error$.next(false);
        this.loading$.startLoading();
    }

    private stopLoading(): void {
        this.loading$.stopLoading();
    }

    private showError(): Observable<never> {
        this.loading$.stopLoading();
        this.error$.next(true);
        return EMPTY;
    }

    private mapParamsToSearch(sortInfo: SortInfo | null, params: Params | null): NotificationSearch {
        if (!params) {
            return {
                startIndex: 0,
                nrOfResults: this.DEFAULT_PAGE_SIZE
            };
        }

        const { page, size } = params || {};

        return {
            startIndex: (page && size && page * size) || 0,
            nrOfResults: size || this.DEFAULT_PAGE_SIZE,
            ...(sortInfo && { sort: sortInfo.field }),
            ...(sortInfo && { direction: sortInfo.direction })
        };
    }

    get selectedItemsCount(): number {
        return [...this.selectedIndexRecordsMap.values()].filter(value => value).length;
    }
}
